import React, { useMemo } from "react";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { buildUser, buildWorkerProfile } from "../../factories";
import {
    EducationLevel,
    ProfileType,
    WorkerProfileInput,
    WorkingPermit,
} from "../../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedProvider } from "@apollo/client/testing";
import CivilStateEditorScreen from "../../../screens/profile-editor/CivilStateEditor";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import { createStackNavigator } from "@react-navigation/stack";
import { UPDATE_WORKER_PROFILE } from "../../../graphql/core";

const Stack = createStackNavigator<{
    CivilStateEdit: undefined;
}>();

const StoryComponent = ({
    workPermit,
    nationality,
}: {
    workPermit?: WorkingPermit;
    nationality: string;
}) => {
    const user = useMemo(
        () =>
            buildUser({
                workPermit,
                workerProfile: buildWorkerProfile({ avsNumber: null }),
                nationality,
            }),
        [nationality, workPermit],
    );

    const mocks = useMemo(
        () => [
            {
                request: {
                    query: UPDATE_WORKER_PROFILE,
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                variableMatcher: () => true,
                result: ({ changes }: { changes: WorkerProfileInput }) => {
                    // eslint-disable-next-line no-console
                    console.log("UPDATE_WORKER_PROFILE.changes", changes);
                    return {
                        data: {
                            updateWorkerProfile: buildWorkerProfile({
                                children: [],
                                hasFiscalAttestation: false,
                                workPermit: WorkingPermit.PermitOther,
                                educationLevel: EducationLevel.Baccalaureate,
                            }),
                        },
                    };
                },
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Worker}>
                <MockedProvider mocks={mocks}>
                    <Stack.Navigator initialRouteName="CivilStateEdit">
                        <Stack.Screen name="CivilStateEdit" component={CivilStateEditorScreen} />
                    </Stack.Navigator>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/ProfileEditor/CivilStateEditor",
    component: StoryComponent,
    argTypes: {
        workPermit: {
            options: Object.values(WorkingPermit),
            control: { type: "radio" },
            defaultValue: WorkingPermit.PermitB,
        },
        nationality: {
            options: ["ch", "fr", "us"],
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const FR_B: Story = { args: { workPermit: WorkingPermit.PermitB, nationality: "fr" } };
export const CH: Story = { args: { workPermit: undefined, nationality: "ch" } };
export const FR_no_permit: Story = { args: { workPermit: undefined, nationality: "fr" } };
